/**
 * Public /r payload for a Deal Brief. Allowlist builder — only the recipient-
 * facing one-pager. No model, cache flags, geo, source provenance (which calls
 * and notes it was built from), or rep-typed context leak — they are
 * structurally absent from the payload, not filtered out of it.
 */
import type {
  CallBriefFlowAlignment,
  CallBriefFlowSide,
  CallBriefResult,
} from '../components/sales-enablement/call-brief-types';

export interface PublicCallBriefSharePayload {
  kind: 'call_brief';
  brief?: {
    company: { name: string; summary: string };
    useCase: string;
    headline: string;
    before: CallBriefFlowSide;
    after: CallBriefFlowSide;
    alignment?: CallBriefFlowAlignment;
    painPoints: string[];
    moxoBenefits: Array<{ title: string; detail: string }>;
    roiNarrative: string;
    whyNow: string[];
    decisionAsk: string;
    reviewedLine: string;
  };
  roi?: {
    scenario: 'conservative';
    annualHoursFreed: number;
    fteEquivalent: number;
    laborSavings: number;
    cycleDaysSaved: number;
    assumptions?: {
      runsPerYear: number;
      peopleInvolved: number;
      hoursSavedPerRun: number;
      loadedHourlyCost: number;
      region: string;
    };
  };
  /** When the brief was prepared — printed in the footer of the shared page. */
  generatedAt?: string;
}

export function buildPublicCallBriefSharePayload(
  result: CallBriefResult,
): PublicCallBriefSharePayload {
  const payload: PublicCallBriefSharePayload = { kind: 'call_brief' };

  const b = result.brief;
  if (b) {
    payload.brief = {
      company: b.company,
      useCase: b.useCase,
      headline: b.headline,
      before: b.before,
      after: b.after,
      ...(b.alignment ? { alignment: b.alignment } : {}),
      painPoints: b.painPoints,
      moxoBenefits: b.moxoBenefits,
      roiNarrative: b.roiNarrative,
      whyNow: b.whyNow,
      decisionAsk: b.decisionAsk,
      reviewedLine: b.reviewedLine,
    };
  }

  const roi = result.roi;
  if (roi) {
    payload.roi = {
      scenario: roi.scenario,
      annualHoursFreed: roi.annualHoursFreed,
      fteEquivalent: roi.fteEquivalent,
      laborSavings: roi.laborSavings,
      cycleDaysSaved: roi.cycleDaysSaved,
      ...(roi.assumptions
        ? {
            assumptions: {
              runsPerYear: roi.assumptions.runsPerYear,
              peopleInvolved: roi.assumptions.peopleInvolved,
              hoursSavedPerRun: roi.assumptions.hoursSavedPerRun,
              loadedHourlyCost: roi.assumptions.loadedHourlyCost,
              region: roi.assumptions.region,
            },
          }
        : {}),
    };
  }

  if (result.generatedAt) payload.generatedAt = result.generatedAt;

  return payload;
}
